import React, { useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Coins, Plus, Trash2, RefreshCw } from "lucide-react";

const CUSTOM = "__custom__";
const fmtRate = (r) => (r == null ? "—" : new Intl.NumberFormat("fr-FR", { maximumFractionDigits: r < 1 ? 4 : 2 }).format(r));

export function I18nCurrenciesCard({ config, mutate, busy }) {
  const [pick, setPick] = useState("");
  const [custom, setCustom] = useState({ code: "", symbol: "", label: "", manual_rate: "" });

  const add = async () => {
    const body = pick === CUSTOM
      ? { code: custom.code.trim().toUpperCase(), symbol: custom.symbol.trim(), label: custom.label.trim(), manual_rate: custom.manual_rate ? Number(String(custom.manual_rate).replace(",", ".")) : null }
      : { code: pick };
    if (!body.code) return;
    const ok = await mutate(() => api.post("/admin/i18n/currencies", body), `Devise ${body.code} ajoutée — les montants se convertissent depuis l'euro`);
    if (ok) { setPick(""); setCustom({ code: "", symbol: "", label: "", manual_rate: "" }); }
  };

  return (
    <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="i18n-currencies-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display font-bold text-lg flex items-center gap-2"><Coins className="h-5 w-5 text-[#059669]" /> Devises</div>
          <div className="text-xs text-[#495057] mt-1">
            Les prix restent en euros ; l'affichage (tarifs, inscription, devis) est converti dans la devise choisie par le visiteur.
            Taux <b>BCE</b> actualisés chaque jour{config.updated_at ? ` (dernière mise à jour ${new Date(config.updated_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })})` : ""}.
          </div>
        </div>
        <Button size="sm" variant="outline" disabled={busy} data-testid="i18n-rates-refresh" className="h-8 text-xs shrink-0"
                onClick={() => mutate(() => api.post("/admin/i18n/refresh-rates"), "Taux BCE actualisés")}>
          <RefreshCw className="h-3 w-3 mr-1" /> Actualiser
        </Button>
      </div>

      <div className="divide-y divide-[#DEE2E6] border border-[#DEE2E6] my-4">
        {config.currencies.map((c) => (
          <div key={c.code} className={`flex items-center gap-3 px-3 py-2 text-sm ${c.enabled === false ? "opacity-50" : ""}`} data-testid={`i18n-cur-row-${c.code}`}>
            <span className="font-mono text-base w-8 text-center">{c.symbol}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{c.label} <span className="font-mono text-[10px] text-[#868E96]">{c.code}</span></div>
              <div className="text-[10px] text-[#495057]" data-testid={`i18n-cur-rate-${c.code}`}>
                {c.code === "EUR" ? "Devise de référence" : <>1 € = <b>{fmtRate(config.rates?.[c.code])}</b> {c.code}{c.manual_rate ? <span className="ml-1 uppercase tracking-wider text-[9px] font-bold text-[#F59E0B]">taux manuel</span> : ""}</>}
              </div>
            </div>
            {c.code === "EUR" ? (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-[#0A0A0A] text-white px-1.5 py-0.5">Référence</span>
            ) : (
              <>
                <Switch checked={c.enabled !== false} disabled={busy} data-testid={`i18n-cur-toggle-${c.code}`}
                        onCheckedChange={(v) => mutate(() => api.patch(`/admin/i18n/currencies/${c.code}`, { enabled: v }), v ? `${c.code} activée` : `${c.code} désactivée`)} />
                <Button size="sm" variant="outline" disabled={busy} data-testid={`i18n-cur-delete-${c.code}`} className="h-7 px-2 text-[#E60000] hover:bg-[#E60000] hover:text-white"
                        onClick={() => { if (window.confirm(`Retirer la devise ${c.code} ?`)) mutate(() => api.delete(`/admin/i18n/currencies/${c.code}`), `${c.code} retirée`); }}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#495057]">Ajouter une devise</div>
        <div className="flex gap-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 h-9 px-2 text-xs border border-[#DEE2E6] bg-white" data-testid="i18n-cur-pick">
            <option value="">Choisir une devise BCE…</option>
            {config.catalog.currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label} (1 € = {fmtRate(c.rate)})</option>)}
            <option value={CUSTOM}>Autre devise (taux manuel)…</option>
          </select>
          <Button onClick={add} disabled={busy || !pick || (pick === CUSTOM && (!custom.code.trim() || !custom.manual_rate))} data-testid="i18n-cur-add" className="h-9 bg-[#059669] text-white">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </div>
        {pick === CUSTOM && (
          <div className="grid grid-cols-4 gap-2" data-testid="i18n-cur-custom">
            <Input value={custom.code} onChange={(e) => setCustom({ ...custom, code: e.target.value })} placeholder="Code (MAD)" maxLength={3} className="h-9 text-xs uppercase" data-testid="i18n-cur-custom-code" />
            <Input value={custom.symbol} onChange={(e) => setCustom({ ...custom, symbol: e.target.value })} placeholder="Symbole" className="h-9 text-xs" data-testid="i18n-cur-custom-symbol" />
            <Input value={custom.label} onChange={(e) => setCustom({ ...custom, label: e.target.value })} placeholder="Libellé" className="h-9 text-xs" data-testid="i18n-cur-custom-label" />
            <Input value={custom.manual_rate} onChange={(e) => setCustom({ ...custom, manual_rate: e.target.value })} placeholder="1 € = …" inputMode="decimal" className="h-9 text-xs" data-testid="i18n-cur-custom-rate" />
          </div>
        )}
      </div>
    </div>
  );
}
